import { getLogger } from '@logtape/logtape';
import z from 'zod';

import { CollapsedAppointmentData } from '@/components/AppointmentScheduler/CollapsedAppointment.tsx';

import { DayjsUnit } from '@/models/Dayjs.ts';
import { EnrichedLocation } from '@/models/EnrichedPrismicDocuments.ts';
import { GenericPatient, PhoneNumberType } from '@/models/GenericPatient.ts';
import { PVSUtilStrategy } from '@/models/PVSStrategy.ts';
import {
    Appointment,
    AppointmentBookStatus,
    AppointmentCancelReason,
    AppointmentType,
    AvailabilityStatus,
    BuildCollectionOptions,
    CollectionConfiguration,
    CollectionOccurrence,
    CollectionStatus,
    CollectionType,
    DailyAppointment,
    Insurance,
    IntervalType,
    PreferredAvailability,
    Priority,
    SeriesPlanningMode,
} from '@/models/appointment/Appointment.ts';
import { AppointmentBookingFormValues } from '@/models/appointment/AppointmentBookingFormValues.ts';
import {
    type ChainAvailabilityItem,
    ChainAvailabilityStatus,
} from '@/models/appointment/AppointmentChainAvailability.ts';
import {
    AppointmentChainConfig,
    ChainGap,
    ChainStep,
    GapUnit,
    MergedGap,
} from '@/models/appointment/AppointmentChainConfig.ts';
import { BookAppointmentDto } from '@/models/appointment/BookAppointmentDto.ts';
import {
    APPOINTMENT_CANCEL_OPTION_TRANSLATIONS,
    CancelAppointmentOptions,
} from '@/models/appointment/CancelAppointmentOptions.ts';
import { EnrichedAppointmentBookingFormValues } from '@/models/appointment/EnrichedAppointmentBookingFormValues.ts';
import {
    EnrichedAppointmentType,
    EnrichedPractitionerData,
    UnMappedScheduleType,
} from '@/models/appointment/EnrichedPractitionerData.ts';
import { GetAppointmentsForTodayResponseDto } from '@/models/appointment/GetAppointmentsForTodayResponseDto.ts';
import { UpdateAppointmentDto } from '@/models/appointment/UpdateAppointmentDto.ts';
import {
    BookAppointmentCollectionDto,
    UpdateAppointmentCollectionDto,
} from '@/models/appointment/book-appointment-collection-dto.ts';
import { CheckinStatus, CheckoutStatus } from '@/models/call-system/models/CallSystemUser.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { UpdatableDataCurEvent } from '@/models/data-cur/dc-event-dto.ts';
import { DCSchedulePartialUpdateDto } from '@/models/doc-cirrus/DCSchedulePartialUpdateDto.ts';
import { ExtractPVSDetailsDto } from '@/models/extract-pvs-details-dto.ts';

import { BookingBatch, EnrichedAppointmentWithBookingStatus } from '@/stores/bookingStore.ts';

import {
    type GapBound,
    compareGapBounds,
    formatExactGapMinutes,
    formatUnitLabel,
    gapInputToIsoDuration,
    parseGapBound,
} from '@/utils/duration-utils.ts';
import { insuranceToInsuranceType } from '@/utils/insurance-utils.ts';
import { getInsuranceByKey, resolveUserId } from '@/utils/patientUtils.ts';
import { filterEnrichedDataByPracticeId } from '@/utils/practitioner/cachedDataUtils.ts';
import { getProfessionalTypeKey, getTextFromRichTextField } from '@/utils/prismicUtils.ts';
import { data_cur_util_strategy } from '@/utils/pvsStrategy/dataCurStrategy.ts';
import { doc_cirrus_util_strategy } from '@/utils/pvsStrategy/docCirrusStrategy.ts';

import { dayjs } from './dayjsSetup.ts';

const logger = getLogger(['util', 'appointment']);

const DETAILS_SEPARATOR = '---SYSTEMDATEN---';

const getPVSStrategy = (provider: PVSProvider): PVSUtilStrategy => {
    switch (provider) {
        case PVSProvider.DOC_CIRRUS: {
            return doc_cirrus_util_strategy;
        }
        case PVSProvider.DATA_CUR: {
            return data_cur_util_strategy;
        }
        default: {
            throw new Error(`Unsupported PVS provider: ${provider}`);
        }
    }
};

export const getPvsPatientId = (appointment?: Appointment): string | undefined => {
    if (!appointment) return undefined;
    const strategy = getPVSStrategy(appointment.pvs_provider);
    return strategy.getPvsPatientId(appointment);
};

export const getEternoPatientId = (appointment?: Appointment): string | undefined => {
    if (!appointment) return undefined;
    return appointment.user_id;
};

export const getScheduleTypeId = (appointment?: Appointment): string | undefined => {
    if (!appointment) return undefined;
    const strategy = getPVSStrategy(appointment.pvs_provider);
    return strategy.getScheduleTypeId(appointment);
};

export const getCalendarId = (appointment?: Appointment): string | undefined => {
    if (!appointment) return undefined;
    const strategy = getPVSStrategy(appointment.pvs_provider);
    return strategy.getCalendarId(appointment);
};

export const getProfessionalId = (appointment?: Appointment): string | undefined => {
    if (!appointment) return undefined;
    const strategy = getPVSStrategy(appointment.pvs_provider);
    return strategy.getProfessionalId(appointment);
};

export const getInstanceId = (appointment?: Appointment): string | undefined => {
    if (!appointment) return undefined;
    const strategy = getPVSStrategy(appointment.pvs_provider);
    return strategy.getInstanceId(appointment);
};

export const getRoomId = (appointment?: Appointment): string | undefined => {
    if (!appointment) return undefined;
    const strategy = getPVSStrategy(appointment.pvs_provider);
    return strategy.getRoomId(appointment);
};

export const appendPatientContext = (message: string, appointment: Appointment): string => {
    const patientName = appointment.data.patient_name?.trim();
    return patientName ? `${message} (Patient: ${patientName})` : message;
};

export const updateRoomId = (appointment: Appointment, room_id: string): Appointment => {
    const strategy = getPVSStrategy(appointment.pvs_provider);
    return strategy.updateRoomId(appointment, room_id);
};

export const joinAppointmentServicesKeys = (appointment: Appointment): string => {
    return appointment.data.services.map((service) => service.key).join(',');
};

/** Service key emitted by the DC sync when a schedule type has no configured Prismic mapping. */
export const UNSET_APPOINTMENT_TYPE_KEY = 'unset';

/**
 * True when the appointment's service type could not be mapped to a Prismic service key.
 * The DC sync emits `key: "unset"` for schedule types with no configured mapping; in that case the
 * required-forms set is unknown, so callers should default to requiring all forms.
 */
export const isAppointmentTypeUnset = (appointment: Appointment): boolean =>
    appointment.data.services.some((service) => service.key === UNSET_APPOINTMENT_TYPE_KEY);

export const generateAppointmentKey = (instance_id?: string, appointment_id?: string): string =>
    `${instance_id}_${appointment_id}`;

export const getAppointmentFromBatch = (
    batches: Map<string, BookingBatch>,
    batchId: string,
    appointmentId: string
): EnrichedAppointmentWithBookingStatus | undefined => {
    const batch = batches.get(batchId);
    if (!batch) {
        return undefined;
    }
    return batch.enriched_appointments.find((value) => value.form_id === appointmentId);
};

export const formToAppointment = (
    apt_forms_data: AppointmentBookingFormValues,
    booking_form_options: EnrichedPractitionerData
): (CollapsedAppointmentData & { calendar_id: string }) | undefined => {
    const filtered_data_by_practice = filterEnrichedDataByPracticeId(
        booking_form_options,
        apt_forms_data.practice_doc_cirrus_id
    );

    const practitioner = filtered_data_by_practice?.practitioners.find((p) =>
        p.calendars.some((c) => c.pvs.id === apt_forms_data.calendar_pvs_id)
    );

    const appointment_type = getAppointmentType(
        apt_forms_data.apt_type_id_or_schedule_type_pvs_id,
        filtered_data_by_practice
    );

    if (!practitioner || !appointment_type) {
        logger.warn('Issue on rendering collapsed appointment');
        return undefined;
    }

    return {
        is_mapped: Boolean(appointment_type),
        practitioner: practitioner,
        type: appointment_type,
        date_time: apt_forms_data.date_time,
        duration: apt_forms_data.duration,
        priority: apt_forms_data.priority,
        description: apt_forms_data.description ?? '',
        info: apt_forms_data.info ?? '',
        insurance: apt_forms_data.insurance,
        calendar_id: apt_forms_data.calendar_pvs_id,
    };
};

const getAppointmentType = (
    type_id: string,
    practitioner_data?: EnrichedPractitionerData
): EnrichedAppointmentType | UnMappedScheduleType | undefined => {
    if (!practitioner_data) return undefined;

    // First try to find in mapped appointment types
    const mapped_type = practitioner_data.appointment_types.find((type) => type.id === type_id);
    if (mapped_type) return mapped_type;

    // Then try to find in unmapped schedule types
    const unmapped_type = practitioner_data.unmapped_schedule_types.find(
        (type) => type.pvs.pvs_schedule_type_id === type_id
    );

    return unmapped_type;
};

export const getBookingAppointmentDtoFromFormData = (
    location: EnrichedLocation,
    patient: {
        user_id: string;
        pvs_user_id: string;
        is_eterno_user: boolean;
    },
    appt_data: CollapsedAppointmentData & {
        calendar_id: string;
    },
    pvs_provider: PVSProvider
): BookAppointmentDto => {
    if (!appt_data.duration || !appt_data.calendar_id || !appt_data.date_time || !appt_data.type) {
        throw new Error(`Missing data on appointment object`);
    }

    const appointment_dto: BookAppointmentDto = {
        appointment: {
            types: [
                {
                    id: appt_data.type.mapped ? appt_data.type.id : appt_data.type.pvs.pvs_schedule_type_id,
                    is_mapped: appt_data.type.mapped,
                    key: 'prismic_key' in appt_data.type ? appt_data.type.prismic_key : '',
                    name: appt_data.type.name,
                },
            ],
            is_eterno_user: patient.is_eterno_user,
            is_at_location: appt_data.type.mapped ? appt_data.type.is_at_location : true,
            is_online: appt_data.type.mapped ? appt_data.type.is_online : false,
            duration: {
                value: appt_data.duration,
                units: 'MINUTES',
            },
            priority: appt_data.priority || Priority.NONE,
            slot: appt_data.date_time,
            description: appt_data.description || '', // Short description of the appointment (user-editable)
            info: appt_data.info || '', // Additional info from the user
        },
        professional: {
            id: appt_data.practitioner.id || '',
            key: getTextFromRichTextField(appt_data.practitioner.prismic_profile?.data.key) || '',
            location_key: location.key,
            type_key: getProfessionalTypeKey(appt_data.practitioner.prismic_professional_type),
        },
        patient: {
            id: patient.user_id, // uuid for registered users or instance_id::pms_user_id for unregistered users
            // dc_user_id: patient.doc_cirrus_user_id,
            is_eterno_user: patient.is_eterno_user,
            is_for_self: true,
            is_returning: false,
            is_public_insurance: appt_data.insurance === Insurance.PUBLIC,
            is_self_payer: [Insurance.SELFPAYER, Insurance.SELF_PAYER].includes(appt_data.insurance),
            is_private_insurance: appt_data.insurance === Insurance.PRIVATE,
            is_with_prescription: false,
            documents: [],
            symptoms: [],
        },
        pvs: { pvs_provider: pvs_provider },
    };

    switch (pvs_provider) {
        case PVSProvider.DOC_CIRRUS: {
            appointment_dto.pvs.doc_cirrus = {
                patient_id: patient.pvs_user_id,
                professional_id: appt_data.practitioner.pvs.practitioner_id,
                schedule_type_id: appt_data.type.pvs.pvs_schedule_type_id ?? '',
                calendar_id: appt_data.calendar_id,
            };
            break;
        }
        case PVSProvider.DATA_CUR: {
            appointment_dto.pvs.data_cur = {
                patient_id: patient.pvs_user_id,
                professional_id: appt_data.practitioner.pvs.practitioner_id,
                event_type_id: appt_data.type.pvs.pvs_schedule_type_id ?? '',
                event_type_code: appt_data.type?.pvs.pvs_event_type_code ?? '',
                resource_id: appt_data.calendar_id,
            };
            break;
        }
        default: {
            break;
        }
    }

    return appointment_dto;
};

// TODO: PVS-MIGRATION cleanup -> more to strategy pattern
export const getUpdateAppointmentDtoFromFormData = (
    data: CollapsedAppointmentData & { calendar_id: string },
    updated_fields: Set<keyof AppointmentBookingFormValues>,
    old_appointment: Appointment,
    send_notification?: boolean
): UpdateAppointmentDto => {
    const update_appointment: UpdateAppointmentDto['appointment'] = {};
    const update_patient: UpdateAppointmentDto['patient'] = {};
    let update_professional: UpdateAppointmentDto['professional'];
    const update_pvs: UpdateAppointmentDto['pvs'] = {
        pvs_provider: old_appointment.pvs_provider,
        doc_cirrus: {},
        data_cur: {},
    };

    // Appointment type info
    if (updated_fields.has('apt_type_id_or_schedule_type_pvs_id')) {
        update_appointment.types = [
            {
                id: data.type.mapped ? data.type.id : data.type.pvs.pvs_schedule_type_id,
                is_mapped: data.type.mapped,
                key: data.type.mapped ? data.type.prismic_key : '',
                name: data.type.name,
            },
        ];

        switch (old_appointment.pvs_provider) {
            case PVSProvider.DOC_CIRRUS: {
                update_pvs.doc_cirrus = {
                    ...update_pvs.doc_cirrus,
                    schedule_type_id: data.type.pvs.pvs_schedule_type_id,
                };
                break;
            }

            case PVSProvider.DATA_CUR: {
                update_pvs.data_cur = {
                    ...update_pvs.data_cur,
                    event_type_id: data.type.pvs.pvs_schedule_type_id,
                    event_type_code: data.type.pvs.pvs_event_type_code,
                };
                break;
            }
            default: {
                break;
            }
        }
    }

    // Professional info
    if (updated_fields.has('calendar_pvs_id')) {
        update_professional = {
            id: data.practitioner.id,
            key: getTextFromRichTextField(data.practitioner.prismic_profile?.data.key) ?? '',
            type_key: getProfessionalTypeKey(data.practitioner.prismic_professional_type),
            dc_user_id: data.practitioner.pvs.practitioner_id, // TODO: PVS-MIGRATION check if this is still used
            calendar_id: data.calendar_id,
        };

        switch (old_appointment.pvs_provider) {
            case PVSProvider.DOC_CIRRUS: {
                update_pvs.doc_cirrus = {
                    ...update_pvs.doc_cirrus,
                    professional_id: data.practitioner.pvs.practitioner_id,
                    calendar_id: data.calendar_id,
                };
                break;
            }

            case PVSProvider.DATA_CUR: {
                update_pvs.data_cur = {
                    ...update_pvs.data_cur,
                    professional_id: data.practitioner.pvs.practitioner_id,
                    resource_id: data.calendar_id,
                };
                break;
            }
            default: {
                break;
            }
        }
    }

    // Appointment info
    if (updated_fields.has('priority')) {
        update_appointment.priority = data.priority;
    }
    if (updated_fields.has('date_time')) {
        update_appointment.start = data.date_time;
    }
    if (updated_fields.has('duration')) {
        update_appointment.duration = { value: data.duration, units: 'MINUTES' };
    }
    if (updated_fields.has('description')) {
        update_appointment.description = data.description;
    }
    if (updated_fields.has('info')) {
        update_appointment.info = data.info;
    }

    // Patient info
    if (updated_fields.has('insurance')) {
        update_patient.insurance = insuranceToInsuranceType(data.insurance);
    }

    if (send_notification !== undefined) {
        update_appointment.send_notification = send_notification;
    }

    const dto: UpdateAppointmentDto = {
        pvs: update_pvs,
        appointment: update_appointment,
        patient: update_patient,
        professional: update_professional,
    };

    return dto;
};

export const isCheckinPending = (checkin_status: CheckinStatus | undefined): boolean => {
    return checkin_status === CheckinStatus.PENDING || !checkin_status;
};

export const isCheckinArrived = (checkin_status: CheckinStatus | undefined): boolean => {
    return checkin_status === CheckinStatus.ARRIVED;
};

export const isCheckedIn = (checkin_status: CheckinStatus | undefined): boolean => {
    return checkin_status === CheckinStatus.CHECKED_IN || checkin_status === CheckinStatus.MANUALLY_CHECKED_IN;
};

export const isCheckedOut = (checkout_status: CheckoutStatus | undefined): boolean => {
    return checkout_status === CheckoutStatus.CHECKED_OUT || checkout_status === CheckoutStatus.MANUALLY_CHECKED_OUT;
};

// Eterno-backed appointments have `is_external` set to false or left undefined; external appointments set it to true.
export const isEternoAppointment = (appointment: Appointment): boolean => {
    return !(appointment.is_external ?? false);
};

export const isFutureAppointment = (now: dayjs.Dayjs, appointment: Appointment): boolean => {
    return dayjs(appointment.end).isSame(now) || dayjs(appointment.end).isAfter(now);
};

/**
 * Resolves the EnrichedAppointmentType for a given appointment from the enriched practitioner data.
 * Canonical PVS-aware lookup — used by both appointmentToForm and the patient modal.
 */
export const getEnrichedAppointmentType = (
    appointment: Appointment,
    enrichedPractitionerData?: EnrichedPractitionerData
): EnrichedAppointmentType | undefined => {
    if (!enrichedPractitionerData) return undefined;

    const calendarId = getCalendarId(appointment) ?? '';

    // TODO: Temporary fix for DataCur until the BE supports event_type_id correctly;
    // until then we match on event_type_code instead of the schedule type id.
    if (appointment.pvs_provider === PVSProvider.DATA_CUR) {
        return enrichedPractitionerData.appointment_types
            .filter((appointment_type) => appointment_type.pvs.pvs_calendar_ids.includes(calendarId))
            .find(
                (appointment_type) =>
                    appointment_type.pvs.pvs_event_type_code === appointment.data_cur_links?.event_type_code
            );
    }

    const pvs_type_id = getScheduleTypeId(appointment) ?? '';
    return enrichedPractitionerData.appointment_types
        .filter((appointment_type) => appointment_type.pvs.pvs_calendar_ids.includes(calendarId))
        .find((appointment_type) => appointment_type.pvs.pvs_schedule_type_id === pvs_type_id);
};

export const isPastAppointment = <T extends { end: string }>(now: dayjs.Dayjs, appointment: T): boolean => {
    return dayjs(appointment.end).isBefore(now);
};

export const getUpdatePartialDtoPVSAppointment = <T extends DCSchedulePartialUpdateDto | UpdatableDataCurEvent>(
    data: Partial<UpdateAppointmentDto>,
    appointment: Appointment
): T => {
    const strategy = getPVSStrategy(appointment.pvs_provider);
    return strategy.getUpdatePartialDtoPVSAppointment(data, appointment) as T;
};

// TODO: maybe move logic to eh-pat-doc-cirrus-sync-api
// This function is an exact copy of the buildDetailsString on the appointments-api
export const buildDetailsString = (
    apt_id: string | undefined,
    user_id: string | undefined,
    services: AppointmentType[],
    info: string,
    location_key: string | undefined,
    ticket: string | undefined,
    online_link: string | undefined,
    is_no_show: boolean | undefined
): string => {
    const services_names: string[] = (services ?? []).map((s) => s.name);
    const services_str: string = (services_names?.length ?? 0) > 1 ? `${services_names.join(',')}\n` : '';

    const online_link_str: string = online_link ? `online_link: ${online_link}\n` : '';
    const ticket_str: string = ticket ? `ticket: ${ticket}\n` : '';

    const user_info_str: string = info === '' ? '' : `${extractDetails(info).userData}`;

    const internal_tags: string[] = [];
    if (apt_id) internal_tags.push(`[apt_id:${apt_id}]`);
    if (user_id) internal_tags.push(`[user_id:${user_id}]`);
    if (location_key) internal_tags.push(`[loc:${location_key}]`);
    if (is_no_show !== undefined) internal_tags.push(`[is_no_show:${is_no_show}]`);

    const has_system_section: boolean = Boolean(
        ticket_str || online_link_str || services_str || internal_tags.length > 0
    );
    if (!has_system_section) {
        return user_info_str;
    }
    const internal_data_str: string = internal_tags.length > 0 ? `\n\n${internal_tags.join('|')}` : '';
    const system_data_str: string =
        `\n\n${DETAILS_SEPARATOR}\n` + `${ticket_str}${online_link_str}${services_str}` + `${internal_data_str}`;
    return `${user_info_str}${system_data_str}`;
};

// This function is an exact copy of the extractDetails on the appointments-api
export const extractDetails = (details: string | null): ExtractPVSDetailsDto => {
    // Handle null case
    if (details === null || details === undefined) {
        return { userData: undefined, internalData: undefined };
    }

    // Handle empty string case
    if (details === '') {
        return { userData: '', internalData: undefined };
    }

    // Check for new format with ---SYSTEMDATEN--- marker (highest priority)
    const system_data_match: RegExpMatchArray | null = details.match(new RegExp(`(${DETAILS_SEPARATOR})`));

    if (system_data_match && system_data_match.index !== undefined) {
        // Extract userData (everything before ---SYSTEMDATEN---)
        const user_data: string = details.slice(0, system_data_match.index).trim();
        // Extract internalData (everything from ---SYSTEMDATEN--- onwards)
        const internal_data: string = details.slice(system_data_match.index).trim();
        return { userData: user_data || undefined, internalData: internal_data };
    }

    // Fall back to legacy format: Check if the string contains newlines followed by brackets
    const has_separated_data: RegExpMatchArray | null = details.match(/\n+\s*\[/);

    if (!has_separated_data) {
        // No separated internal data found - return entire string as userData
        return { userData: details, internalData: undefined };
    }

    // Extract user detail (everything before newlines + brackets)
    const user_regex: RegExp = /^(.*?)\n+\s*\[/s;
    const user_match: RegExpMatchArray | null = details.match(user_regex);
    const user_data: string | undefined = user_match ? user_match[1] : undefined;

    // Extract internal metadata (bracketed data after newlines)
    const internal_regex: RegExp = /\n+\s*(\[.*)/s;
    const internal_match: RegExpMatchArray | null = details.match(internal_regex);
    const internal_data: string | undefined = internal_match ? internal_match[1].trim() : undefined;

    return { userData: user_data, internalData: internal_data };
};

export const appointmentToForm = (
    appointment: DailyAppointment,
    enriched_practitioner_data?: EnrichedPractitionerData
): AppointmentBookingFormValues => {
    // Get pvs type id
    const pvs_type_id: string = getScheduleTypeId(appointment) ?? '';

    if (appointment.pvs_provider === PVSProvider.DOC_CIRRUS && (!pvs_type_id || pvs_type_id === '')) {
        throw new Error('PVS type id is missing');
    }

    // Get appointment type if mapped (PVS-aware lookup shared with getEnrichedAppointmentType).
    const appointment_type: EnrichedAppointmentType | undefined = getEnrichedAppointmentType(
        appointment,
        enriched_practitioner_data
    );

    const practice_doc_cirrus_id: string =
        !!enriched_practitioner_data && enriched_practitioner_data.pvs_provider === PVSProvider.DOC_CIRRUS
            ? (enriched_practitioner_data?.practitioners?.find(({ id }) => id === appointment.professional.id)
                  ?.practices[0].pvs?.id ?? '')
            : '';

    const set_form_values: AppointmentBookingFormValues = {
        pvs_provider: appointment.pvs_provider,
        apt_type_id_or_schedule_type_pvs_id: appointment_type?.id ?? pvs_type_id, // TODO: support multi service appointment types
        calendar_pvs_id: getCalendarId(appointment) ?? '',
        date_time: appointment.start,
        duration: appointment.duration.value,
        insurance:
            getInsuranceByKey(
                appointment.data.insurance_coverage_type?.toUpperCase() ?? appointment.patient?.insurance?.type
            ) ?? Insurance.PUBLIC,
        description: appointment.description,
        info: appointment.data.info,
        is_offered: true,
        practice_doc_cirrus_id: practice_doc_cirrus_id,
        priority: appointment.priority ?? Priority.NONE,
    };

    return set_form_values;
};

export const createAppointmentDtoFromForm = (
    form_value: EnrichedAppointmentBookingFormValues,
    forms_options: EnrichedPractitionerData,
    location: EnrichedLocation,
    patient?: GenericPatient
): BookAppointmentDto | undefined => {
    const data = formToAppointment(form_value, forms_options);
    if (!data || !patient) return undefined;

    const resolvedUser = resolveUserId(form_value.eterno_user_id, patient.instance_id, form_value.pvs_user_id);
    return getBookingAppointmentDtoFromFormData(
        location,
        {
            // We use this method to either use the eternoUserId resolved from the insurance validation or the pvsPatientId to build the userId for the booking
            user_id: resolvedUser.id,
            pvs_user_id: form_value.pvs_user_id,
            is_eterno_user: resolvedUser.isEternoUser,
        },
        data,
        form_value.pvs_provider
    );
};

export const MAX_SERIES_SPAN_DAYS = 1800;

const DAYS_PER_INTERVAL: Record<IntervalType, number> = {
    [IntervalType.DAILY]: 1,
    [IntervalType.WEEKLY]: 7,
    [IntervalType.MONTHLY]: 30,
};

export const getSeriesSpanInDays = (config: CollectionConfiguration): number =>
    (config.number_of_appointments - 1) * config.interval_frequency * DAYS_PER_INTERVAL[config.interval_type];

const resolveSeriesStartDate = (config: CollectionConfiguration, occurrences: CollectionOccurrence[]): string => {
    if (config.planning_mode !== SeriesPlanningMode.AUTO) return config.start_date;
    const earliest = occurrences.map((occurrence) => occurrence.start_datetime).sort((a, b) => a.localeCompare(b))[0];
    return earliest ? dayjs.utc(earliest).format('YYYY-MM-DD') : config.start_date;
};

export const buildCollectionDto = (
    baseDto: BookAppointmentDto,
    config: CollectionConfiguration,
    occurrences: CollectionOccurrence[],
    instanceId: string,
    options: BuildCollectionOptions
): BookAppointmentCollectionDto => ({
    collection_data: {
        instance_id: instanceId,
        type: CollectionType.SERIES,
        booking_status: CollectionStatus.PENDING,
        series_config: {
            interval_type: config.interval_type,
            professional_id: baseDto.professional.id,
            interval_frequency: config.interval_frequency,
            start_date: resolveSeriesStartDate(config, occurrences),
            preferred_availability: config.preferred_availability,
            preferred_time:
                config.preferred_availability === PreferredAvailability.FIXED ? config.preferred_time : undefined,
            number_of_appointments: config.number_of_appointments,
        },
    },
    patient: baseDto.patient,
    appointments: occurrences.map((occ) => ({
        professional: baseDto.professional,
        appointment: { ...baseDto.appointment, slot: occ.start_datetime },
        pvs: baseDto.pvs,
    })),
    ...(options.isOverlap !== undefined && { is_overlap: options.isOverlap }),
});

export const buildUpdateCollectionDto = (
    baseDto: BookAppointmentDto,
    config: CollectionConfiguration,
    occurrences: CollectionOccurrence[],
    instanceId: string,
    collectionId: string,
    sendNotification?: boolean
): UpdateAppointmentCollectionDto => ({
    collection_data: {
        id: collectionId,
        instance_id: instanceId,
        type: CollectionType.SERIES,
        series_config: {
            interval_type: config.interval_type,
            interval_frequency: config.interval_frequency,
            start_date: resolveSeriesStartDate(config, occurrences),
            preferred_availability: config.preferred_availability,
            preferred_time:
                config.preferred_availability === PreferredAvailability.FIXED ? config.preferred_time : undefined,
            number_of_appointments: config.number_of_appointments,
        },
    },
    patient: baseDto.patient,
    appointments: occurrences.map((occ) => ({
        professional: baseDto.professional,
        appointment: { ...baseDto.appointment, slot: occ.start_datetime },
        pvs: baseDto.pvs,
    })),
    send_notification: sendNotification,
});

type PvsUserIdResult =
    | { success: true; userId: string; eternoUserId?: string }
    | { success: false; errorMessage: string };

export const resolveDocCirrusPvsUserId = (result: {
    forms: { pvs_user_id?: string; eterno_user_id?: string }[];
    duplication_required: boolean;
    duplication_done: boolean;
}): PvsUserIdResult => {
    if (result.duplication_required && !result.duplication_done) {
        return {
            success: false,
            errorMessage: 'Patientenduplizierung erforderlich. Bitte einzelnen Termin buchen.',
        };
    }
    const pvsUserId = result.forms[0]?.pvs_user_id?.trim();
    if (!pvsUserId) {
        return { success: false, errorMessage: 'Fehler bei der Ermittlung der Versicherungsdaten.' };
    }
    return { success: true, userId: pvsUserId, eternoUserId: result.forms[0]?.eterno_user_id?.trim() };
};

export const mergeAppointments = (
    responses: GetAppointmentsForTodayResponseDto[]
): GetAppointmentsForTodayResponseDto => {
    const active_appointments: DailyAppointment[] = [];
    const inactive_appointments: DailyAppointment[] = [];
    let total = 0;
    let active_total = 0;
    let inactive_total = 0;
    for (const response of responses) {
        active_appointments.push(...response.data.active);
        inactive_appointments.push(...response.data.inactive);
        total += response.meta.total;
        active_total += response.meta.active_total;
        inactive_total += response.meta.inactive_total;
    }

    return {
        data: { active: active_appointments, inactive: inactive_appointments },
        meta: { total: total, active_total: active_total, inactive_total: inactive_total },
    };
};

export const enrichAppointmentsListWithIsEternoUser = (appointments: DailyAppointment[]): void => {
    for (const appointment of appointments) {
        if (!appointment.is_eterno_user) {
            appointment.is_eterno_user = z.guid().safeParse(appointment.user_id).success;
        }
    }
};

export const removeCancelledAppointments = (
    appointments: DailyAppointment[],
    show_cancelled_today?: boolean
): DailyAppointment[] | undefined => {
    const today = dayjs();

    const appointments_filtered = appointments.filter(
        (appointment) =>
            appointment.status !== AppointmentBookStatus.CANCELLED ||
            // If we want to show todays cancelled appointment
            (show_cancelled_today &&
                // then check if the appointment is for today and it was cancelled today
                dayjs(appointment.meta.updated_at).isSame(today, DayjsUnit.DAY) &&
                dayjs(appointment.start).isSame(today, DayjsUnit.DAY))
    );

    return appointments_filtered;
};

export const getGenericPatientFromAppointment = (appointment: DailyAppointment): GenericPatient | undefined => {
    if (!appointment.patient) return undefined;

    return {
        pvs_user_id: appointment.patient.pvs.user_id,
        eterno_user_id: appointment.patient.id,
        is_registered: appointment.is_eterno_user ?? z.guid().safeParse(appointment.patient.id).success,
        instance_id: appointment.patient.pvs.instance_id,
        patient_number: appointment.patient.account.patient_number,
        pvs_provider: appointment.pvs_provider,
        personal: {
            salutation: appointment.patient.name.salutation,
            first_name: appointment.patient.name.first_name,
            last_name: appointment.patient.name.last_name,
            date_of_birth: appointment.patient.account.date_of_birth,
            gender: appointment.patient.name.gender,
        },
        address: {
            house_number: '',
            street: appointment.patient.address?.street ?? '',
            addon: appointment.patient.address?.addon,
            zip: appointment.patient.address?.zip ?? '',
            city: appointment.patient.address?.city ?? '',
            country: appointment.patient.address?.country ?? '',
            country_code: appointment.patient.address?.country_code ?? '',
        },
        contact: {
            emails: appointment.patient.contact.email
                ? [{ is_preferred: true, value: appointment.patient.contact.email }]
                : [],
            phone_numbers: appointment.patient.contact.phone
                ? [{ is_preferred: true, type: PhoneNumberType.MOBILE, value: appointment.patient.contact.phone }]
                : [],
        },
        insurances:
            appointment.patient.insurance?.name && appointment.patient.insurance?.type
                ? [{ name: appointment.patient.insurance.name, type: appointment.patient.insurance.type }]
                : [],
    };
};

export const groupAppointmentsBySeries = (appointments: Appointment[]): Record<string, Appointment[]> => {
    const grouped: Record<string, Appointment[]> = {};
    for (const apt of appointments) {
        const key = apt.collection_id || `single-${apt.id}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(apt);
    }
    return grouped;
};

export const isSeriesUpdateCancelledAppointment = (appointment: Appointment): boolean => {
    return appointment.cancel_reason === AppointmentCancelReason.COLLECTION_UPDATE;
};

export const getAppointmentsExcludingSeriesUpdateCancelled = (appointments: Appointment[]): Appointment[] => {
    return appointments.filter((appointment) => !isSeriesUpdateCancelledAppointment(appointment));
};

// Returns true if every appointment in the array is canceled or is in the past, but at least one needs to be canceled
export const isSeriesInactiveDueToCancellation = (appointments: Appointment[], now: dayjs.Dayjs): boolean => {
    if (appointments.length === 0) return false;
    const allCanceledOrPast = appointments.every(
        (appointment) => isCanceledAppointment(appointment) || isPastAppointment(now, appointment)
    );
    const atLeastOneCanceled = appointments.some((appointment) => isCanceledAppointment(appointment));
    return allCanceledOrPast && atLeastOneCanceled;
};

export const getAppointmentCancelReasonTranslation = (cancel_reason?: AppointmentCancelReason): string | undefined => {
    switch (cancel_reason) {
        case AppointmentCancelReason.PATIENT_CANCELLED: {
            return APPOINTMENT_CANCEL_OPTION_TRANSLATIONS[CancelAppointmentOptions.PATIENT_CANCELLED];
        }
        case AppointmentCancelReason.DOCTOR_CANCELLED: {
            return APPOINTMENT_CANCEL_OPTION_TRANSLATIONS[CancelAppointmentOptions.DOCTOR_CANCELLED];
        }
        case AppointmentCancelReason.OTHER: {
            return APPOINTMENT_CANCEL_OPTION_TRANSLATIONS[CancelAppointmentOptions.OTHER];
        }
        default: {
            return undefined;
        }
    }
};

export const isCanceledAppointment = (appointment: Appointment): boolean => {
    return (
        !!appointment.cancel_reason ||
        appointment.is_cancellation_confirmed ||
        appointment.status === AppointmentBookStatus.CANCELLED
    );
};

export const isDuplicateOccurrence = (
    occurrences: CollectionOccurrence[],
    currentIndex: number,
    slot: string
): boolean => {
    const slotTime = dayjs(slot);
    return occurrences.some((o, idx) => idx !== currentIndex && dayjs(o.start_datetime).isSame(slotTime, 'minute'));
};

export const hasOccurrenceConflict = (occurrences: CollectionOccurrence[], index: number): boolean => {
    if (!occurrences[index]) return false;
    return (
        occurrences[index].availability === AvailabilityStatus.UNAVAILABLE ||
        isDuplicateOccurrence(occurrences, index, occurrences[index].start_datetime)
    );
};

export const getEffectiveGap = (
    allGaps: ChainGap[],
    fromOrder: number,
    toOrder: number,
    allOriginalOrders: number[]
): MergedGap => {
    const ordersInRange = allOriginalOrders.filter((o) => o >= fromOrder && o <= toOrder).sort((a, b) => a - b);

    let mergedMin: GapBound | undefined;
    let mergedMax: GapBound | undefined;
    let hasUnconstrained = false;
    let hasConstrainedGap = false;

    for (let i = 0; i < ordersInRange.length - 1; i++) {
        const gap = allGaps.find((g) => g.from_step === ordersInRange[i] && g.to_step === ordersInRange[i + 1]);
        if (!gap) {
            hasUnconstrained = true;
            continue;
        }
        hasConstrainedGap = true;
        const min = parseGapBound(gap.min_duration, false);
        if (!mergedMin || compareGapBounds(min.value, min.unit, mergedMin.value, mergedMin.unit) < 0) mergedMin = min;
        if (gap.max_duration === undefined) {
            hasUnconstrained = true;
        } else {
            const max = parseGapBound(gap.max_duration, false);
            if (!mergedMax || compareGapBounds(max.value, max.unit, mergedMax.value, mergedMax.unit) > 0)
                mergedMax = max;
        }
    }

    const maxBound = mergedMax ?? { value: 0, unit: GapUnit.DAYS };
    return {
        min_duration: hasConstrainedGap && mergedMin ? gapInputToIsoDuration(mergedMin.value, mergedMin.unit) : 'P0D',
        max_duration: hasUnconstrained ? undefined : gapInputToIsoDuration(maxBound.value, maxBound.unit),
    };
};

// Builds a Map<collection_id, count> of non-canceled future chain appointments.
// Keyed by collection_id (the actual grouper) — multiple collections can share the same chain_configuration_id.
// Use this once and do O(1) lookups via `.get()` instead of repeatedly filtering.
export const buildChainFutureCountMap = (futureAppointments?: Appointment[]): Map<string, number> => {
    const countMap = new Map<string, number>();
    if (!futureAppointments) return countMap;
    for (const apt of futureAppointments) {
        if (apt.collection_id && apt.chain_configuration_id && !isCanceledAppointment(apt)) {
            countMap.set(apt.collection_id, (countMap.get(apt.collection_id) ?? 0) + 1);
        }
    }
    return countMap;
};

export const isSeriesMode = (appointment: Appointment): boolean => {
    return Boolean(appointment.collection_id && !appointment.chain_configuration_id);
};

export const isChainMode = (appointment: Appointment): boolean => {
    return Boolean(appointment.collection_id && appointment.chain_configuration_id);
};

export type ChainSlotStatus =
    | { kind: 'available' }
    | {
          kind: 'out-of-gap';
          actual_days: number;
          actual_minutes: number;
          min_duration: string;
          max_duration: string | undefined;
      }
    | { kind: 'not-available' };

const daysBetweenIsoStarts = (fromIso: string, toIso: string): number =>
    dayjs(toIso).startOf('day').diff(dayjs(fromIso).startOf('day'), 'day');

const minutesBetweenPrevEndAndStart = (prevEndIso: string, startIso: string): number =>
    Math.round(dayjs(startIso).diff(dayjs(prevEndIso), 'minute', true));

export const computeChainSlotStatus = (
    availability: ChainAvailabilityItem,
    previous: ChainAvailabilityItem | undefined,
    previousStep: ChainStep | undefined,
    currentStep: ChainStep,
    chainGaps: AppointmentChainConfig['gaps'],
    allOriginalOrders: number[]
): ChainSlotStatus => {
    if (availability.status === ChainAvailabilityStatus.NOT_AVAILABLE) {
        return { kind: 'not-available' };
    }

    if (!previous || !previousStep) {
        return { kind: 'available' };
    }

    // Can't validate gap against a NOT_AVAILABLE previous — its start_date is a backend placeholder.
    if (previous.status !== ChainAvailabilityStatus.AVAILABLE) {
        return { kind: 'available' };
    }

    const gap = getEffectiveGap(chainGaps, previousStep.order, currentStep.order, allOriginalOrders);
    const min = parseGapBound(gap.min_duration, false);
    const max = gap.max_duration ? parseGapBound(gap.max_duration, false) : undefined;
    const actualDays = daysBetweenIsoStarts(previous.start_date, availability.start_date);
    const actualMinutes = minutesBetweenPrevEndAndStart(previous.end_date, availability.start_date);
    const actualForUnit = (unit: GapUnit): number => (unit === GapUnit.DAYS ? actualDays : actualMinutes);

    const outOfGap: ChainSlotStatus = {
        kind: 'out-of-gap',
        actual_days: actualDays,
        actual_minutes: actualMinutes,
        min_duration: gap.min_duration,
        max_duration: gap.max_duration,
    };

    if (actualMinutes < 0) return outOfGap;
    if (actualForUnit(min.unit) < min.value) return outOfGap;
    if (max && actualForUnit(max.unit) > max.value) return outOfGap;

    return { kind: 'available' };
};

export const computeChainStatuses = (
    availability: ChainAvailabilityItem[],
    visibleSteps: ChainStep[],
    selectedChain: AppointmentChainConfig | undefined
): ChainSlotStatus[] => {
    const allOriginalOrders = selectedChain?.steps.map((s) => s.order) ?? [];
    const availabilityByOrder = new Map(availability.map((item) => [item.step_order, item]));
    return visibleSteps.map((currentStep, index) => {
        const item = availabilityByOrder.get(currentStep.order);
        if (!item) return { kind: 'not-available' };
        const previousStep = visibleSteps[index - 1];
        const previousItem = previousStep ? availabilityByOrder.get(previousStep.order) : undefined;
        return computeChainSlotStatus(
            item,
            previousItem,
            previousStep,
            currentStep,
            selectedChain?.gaps ?? [],
            allOriginalOrders
        );
    });
};

export const countUnavailableSteps = (statuses: ChainSlotStatus[]): number =>
    statuses.filter((s) => s.kind === 'not-available').length;

type OutOfGapStatus = Extract<ChainSlotStatus, { kind: 'out-of-gap' }>;

const formatBound = ({ unit, value }: GapBound): string => formatUnitLabel(value, unit, 'de');

const formatActualGap = (status: OutOfGapStatus): string => {
    const min = parseGapBound(status.min_duration, false);
    const max = status.max_duration ? parseGapBound(status.max_duration, false) : undefined;
    const actualForUnit = (unit: GapUnit): number =>
        unit === GapUnit.DAYS ? status.actual_days : status.actual_minutes;
    const violatedUnit = max && actualForUnit(max.unit) > max.value ? max.unit : min.unit;

    return violatedUnit === GapUnit.DAYS
        ? formatUnitLabel(status.actual_days, GapUnit.DAYS, 'de')
        : formatExactGapMinutes(status.actual_minutes, 'de');
};

const formatRangeLowerBound = (min: GapBound, max: GapBound): string => {
    if (min.value === 0) return '0';
    if (min.unit === max.unit) return `${min.value}`;
    return formatBound(min);
};

const formatAllowedGap = (status: OutOfGapStatus): string => {
    const min = parseGapBound(status.min_duration);
    const max = status.max_duration ? parseGapBound(status.max_duration) : undefined;

    if (max === undefined) {
        return min.unit === GapUnit.DAYS && min.value !== 1
            ? `Erlaubt sind: ab ${min.value} Tagen`
            : `Erlaubt sind: ab ${formatBound(min)}`;
    }
    if (min.unit === max.unit && min.value === max.value) {
        return `Erlaubt ${max.value === 1 ? 'ist' : 'sind'}: ${formatBound(max)}`;
    }
    return `Erlaubt sind: ${formatRangeLowerBound(min, max)} - ${formatBound(max)}`;
};

export const formatGapWarningText = (status: OutOfGapStatus): string => {
    if (status.actual_minutes < 0) return 'Terminreihenfolge vertauscht!';

    const actual = formatActualGap(status);
    const actualSentence = actual.endsWith('.') ? actual : `${actual}.`;
    return `Abstand zum vorherigen Termin: ${actualSentence} ${formatAllowedGap(status)}`;
};

export const applySlotUpdate = (
    availability: ChainAvailabilityItem[],
    stepOrder: number,
    slotIso: string,
    durationMinutes: number
): ChainAvailabilityItem[] => {
    const start = dayjs(slotIso);
    const end = start.add(durationMinutes, 'minute');
    return availability.map((item) =>
        item.step_order === stepOrder
            ? {
                  ...item,
                  status: ChainAvailabilityStatus.AVAILABLE,
                  start_date: start.toISOString(),
                  end_date: end.toISOString(),
              }
            : item
    );
};

// Backend names look like "Last, First, DD.MM.YYYY, ID:<n>, <appointment type>". Strip everything up to
// the "ID:<n>," marker so only the appointment type remains, keeping any commas it may itself contain.
export const titleAfterId = (label: string): string => {
    const match = /ID:[^,]*,\s*(.*)/.exec(label);
    return match ? match[1].trim() : label;
};
